/* ============================================================
   Mark Ryden Wholesale - Overlay Queue

   One controller for every overlay in the overlay-group.
   Replaces the old blind setTimeout, which fired the bulk-offer
   popup 8s after load regardless of what else was on screen.

   Rules:
     - one overlay on screen at a time, lowest tier wins
     - nothing opens until the consent bar has been answered
     - 45s cooldown after any overlay closes
     - 2 modal impressions per session, maximum
     - dismiss suppresses for N days, submit suppresses for good
     - never on cart / checkout / account / contact
   ============================================================ */

(function () {
  'use strict';

  var CAP = 2;
  var COOLDOWN = 45000;
  var BLOCKED = ['/cart', '/checkout', '/account', '/pages/contact'];
  var CONSENT_COOKIE = 'bls_cookie_consent';

  var overlays = [];
  var current = null;
  var modalsShown = 0;
  var lastClosedAt = 0;
  var lastFocus = null;

  function inDesignMode() { return !!(window.Shopify && window.Shopify.designMode); }

  function onBlockedPath() {
    var p = window.location.pathname;
    for (var i = 0; i < BLOCKED.length; i++) {
      if (p.indexOf(BLOCKED[i]) === 0) return true;
    }
    return false;
  }

  function consentAnswered() {
    return document.cookie.indexOf(CONSENT_COOKIE + '=') > -1;
  }

  function storeKey(id) { return 'mrw_ov_' + id; }

  function isSuppressed(id) {
    try {
      var v = localStorage.getItem(storeKey(id));
      if (!v) return false;
      if (v === 'done') return true;
      return Date.now() < parseInt(v, 10);
    } catch (e) { return false; }
  }

  function suppress(id, days) {
    try {
      if (days === 'done') localStorage.setItem(storeKey(id), 'done');
      else localStorage.setItem(storeKey(id), String(Date.now() + days * 86400000));
    } catch (e) {}
  }

  var pageViews = (function () {
    try {
      var n = parseInt(sessionStorage.getItem('mrw_pv') || '0', 10) + 1;
      sessionStorage.setItem('mrw_pv', String(n));
      return n;
    } catch (e) { return 1; }
  })();

  /* ---------- open / close ---------- */

  function openOverlay(ov) {
    if (current) return;
    current = ov;
    ov.el.classList.add('is-open');
    if (ov.modal) {
      modalsShown++;
      lastFocus = document.activeElement;
      document.documentElement.classList.add('mrw-locked');
      var first = ov.el.querySelector('input, button, a[href]');
      if (first) setTimeout(function () { try { first.focus(); } catch (e) {} }, 60);
    }
  }

  function closeOverlay(ov, days) {
    ov.el.classList.remove('is-open');
    if (ov.modal) {
      document.documentElement.classList.remove('mrw-locked');
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }
    if (typeof days !== 'undefined') suppress(ov.id, days);
    if (current === ov) current = null;
    lastClosedAt = Date.now();
    setTimeout(pump, COOLDOWN + 50);
  }

  function isEligible(ov) {
    if (!ov.ready) return false;
    if (isSuppressed(ov.id)) return false;
    if (!consentAnswered()) return false;
    if (ov.modal && modalsShown >= CAP) return false;
    if (lastClosedAt && Date.now() - lastClosedAt < COOLDOWN) return false;
    return true;
  }

  function pump() {
    if (current || onBlockedPath()) return;
    var best = null;
    for (var i = 0; i < overlays.length; i++) {
      var ov = overlays[i];
      if (!isEligible(ov)) continue;
      if (!best || ov.tier < best.tier) best = ov;
    }
    if (best) openOverlay(best);
  }

  /* ---------- triggers ---------- */

  function armTrigger(ov) {
    var t = ov.el.getAttribute('data-trigger') || 'engaged';

    if (t === 'immediate') {
      ov.ready = true;
      return;
    }

    if (t === 'engaged') {
      // second pageview, or 30s AND 40% scroll depth
      if (pageViews >= 2) { ov.ready = true; return; }
      var waited = false, scrolled = false;
      setTimeout(function () { waited = true; check(); }, 30000);
      window.addEventListener('scroll', function onScroll() {
        var h = document.body.scrollHeight - window.innerHeight;
        if (h > 0 && (window.pageYOffset / h) >= 0.4) {
          scrolled = true;
          window.removeEventListener('scroll', onScroll);
          check();
        }
      }, { passive: true });
      function check() { if (waited && scrolled) { ov.ready = true; pump(); } }
      return;
    }

    if (t === 'exit') {
      if (window.location.pathname.indexOf('/products/') === -1) return;
      setTimeout(function () {
        document.addEventListener('mouseout', function onOut(e) {
          if (e.clientY > 0 || e.relatedTarget) return;
          document.removeEventListener('mouseout', onOut);
          ov.ready = true;
          pump();
        });
      }, 20000);
      return;
    }

    if (t === 'cart') {
      var count = parseInt(ov.el.getAttribute('data-cart-count') || '0', 10);
      if (count > 0 && pageViews > 1) {
        setTimeout(function () { ov.ready = true; pump(); }, 8000);
      }
      return;
    }
  }

  /* ---------- wiring ---------- */

  function register(el) {
    var ov = {
      el: el,
      id: el.getAttribute('data-id') || 'overlay',
      tier: parseInt(el.getAttribute('data-tier') || '2', 10),
      modal: el.getAttribute('data-modal') === 'true',
      days: parseInt(el.getAttribute('data-suppress-days') || '30', 10),
      ready: false
    };
    overlays.push(ov);

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-mrw-close]')) {
        e.preventDefault();
        closeOverlay(ov, ov.days);
      } else if (e.target.classList.contains('mrw-ov__scrim')) {
        closeOverlay(ov, ov.days);
      }
    });

    var form = el.querySelector('[data-mrw-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var body = 'form_type=contact&utf8=%E2%9C%93';
        var email = form.querySelector('[name="email"]');
        var extra = [];
        Array.prototype.forEach.call(form.querySelectorAll('input, select, textarea'), function (f) {
          if (!f.name || f.name === 'email' || !f.value) return;
          extra.push(f.name + ': ' + f.value);
        });
        if (email) body += '&contact[email]=' + encodeURIComponent(email.value);
        body += '&contact[body]=' + encodeURIComponent(
          (form.getAttribute('data-subject') || 'Overlay enquiry') +
          (extra.length ? ' - ' + extra.join(' / ') : '')
        );
        fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body
        });
        el.classList.add('is-done');
        suppress(ov.id, 'done');
        setTimeout(function () { closeOverlay(ov); }, 2200);
      });
    }

    if (inDesignMode()) return; // the editor drives these, not the queue
    armTrigger(ov);
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-mrw-overlay]');
    for (var i = 0; i < nodes.length; i++) register(nodes[i]);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) closeOverlay(current, current.days);
    });

    // manual openers anywhere on the page
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-mrw-open]');
      if (!t) return;
      e.preventDefault();
      var want = t.getAttribute('data-mrw-open');
      for (var i = 0; i < overlays.length; i++) {
        if (overlays[i].id === want) { if (current) closeOverlay(current); openOverlay(overlays[i]); return; }
      }
    });

    if (inDesignMode()) {
      document.addEventListener('shopify:section:select', function (e) {
        var el = e.target.querySelector('[data-mrw-overlay]');
        if (!el) return;
        if (current) closeOverlay(current);
        for (var i = 0; i < overlays.length; i++) {
          if (overlays[i].el === el) { openOverlay(overlays[i]); return; }
        }
      });
      document.addEventListener('shopify:section:deselect', function () {
        if (current) closeOverlay(current);
      });
      return;
    }

    if (consentAnswered()) {
      pump();
    } else {
      // the consent bar holds tier 0 - poll until the visitor answers it
      var waitForConsent = setInterval(function () {
        if (consentAnswered()) { clearInterval(waitForConsent); setTimeout(pump, 800); }
      }, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
